import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

/**
 * LossTriangleRenderer — renders a loss development triangle with factors and ultimates.
 */
export const LossTriangleRenderer = React.memo(function LossTriangleRenderer({ analysis, vizConfig, formatValue, onFactorMethodChange, onManualFactor, onTailFactorChange }) {
  const [showProjections, setShowProjections] = useState(true);
  
  if (!analysis || analysis.error) {
    return <p className="text-xs text-muted-foreground text-center py-8">{analysis?.error || 'Configure origin, development period, and value fields to build a triangle'}</p>;
  }

  const cfg = vizConfig || {};
  const { triangle, factors, ultimates } = analysis;
  const { origins, dev_periods, triangle_data, diagonal } = triangle;
  const { all_methods, selected_factors, dev_pairs, factor_columns } = factors;
  const { projections, totals } = ultimates;

  const cellPad = cfg.compact ? 'px-2 py-0.5' : 'px-3 py-1.5';
  const fontSize = cfg.compact ? 'text-[9px]' : 'text-[11px]';

  const isDiagonal = (origin, dev) => {
    const d = diagonal[origin];
    return d && String(d.dev) === String(dev);
  };

  const isProjected = (origin, dev) => {
    const d = diagonal[origin];
    if (!d) return false;
    return Number(dev) > Number(d.dev);
  };

  const getProjectedValue = (origin, dev) => {
    const proj = projections[origin];
    if (!proj) return null;
    const step = proj.development_path?.find(s => String(s.dev) === String(dev));
    return step?.value;
  };

  return (
    <div className="overflow-auto w-full h-full" data-testid="loss-triangle">
      {/* Triangle Matrix */}
      <table className={`w-full border-collapse ${fontSize}`}>
        <thead className="sticky top-0 z-10">
          <tr className="bg-muted/40 border-b border-border/50">
            <th className={`${cellPad} text-[10px] font-semibold text-muted-foreground text-left uppercase tracking-wider bg-muted/40 sticky left-0 z-20`}>
              {triangle.origin_field?.replace(/_/g, ' ') || 'Origin'}
            </th>
            {dev_periods.map(dev => (
              <th key={dev} className={`${cellPad} text-[10px] font-semibold text-foreground text-right border-r border-border/20 bg-muted/30`}>
                {dev}
              </th>
            ))}
            {showProjections && (
              <>
                <th className={`${cellPad} text-[10px] font-semibold text-emerald-400 text-right bg-emerald-500/10 border-l-2 border-emerald-500/20`}>Ultimate</th>
                <th className={`${cellPad} text-[10px] font-semibold text-amber-400 text-right bg-amber-500/10`}>IBNR</th>
                <th className={`${cellPad} text-[10px] font-semibold text-primary text-right bg-primary/10`}>CDF</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {origins.map((origin, ri) => (
            <tr key={origin} className={`border-b border-border/20 ${ri % 2 === 1 ? 'bg-muted/10' : ''}`}>
              <td className={`${cellPad} font-medium text-foreground sticky left-0 bg-background border-r border-border/30 z-10`}>
                {origin}
              </td>
              {dev_periods.map(dev => {
                const row = triangle_data[ri];
                const val = row?.[String(dev)];
                const isDiag = isDiagonal(origin, dev);
                const isProj = isProjected(origin, dev);
                const projVal = isProj ? getProjectedValue(origin, dev) : null;
                const displayVal = val !== null && val !== undefined ? val : projVal;
                
                return (
                  <td key={dev} className={`${cellPad} text-right tabular-nums border-r border-border/10 ${
                    isDiag ? 'bg-primary/15 font-semibold text-primary border-2 border-primary/30' :
                    isProj && projVal !== null ? 'bg-emerald-500/5 text-emerald-400/70 italic' :
                    val !== null && val !== undefined ? 'text-foreground/80' : 'text-muted-foreground/20'
                  }`}>
                    {displayVal !== null && displayVal !== undefined ? formatValue(displayVal) : ''}
                  </td>
                );
              })}
              {showProjections && projections[origin] && (
                <>
                  <td className={`${cellPad} text-right font-semibold text-emerald-400 bg-emerald-500/5 border-l-2 border-emerald-500/20 tabular-nums`}>
                    {formatValue(projections[origin].ultimate)}
                  </td>
                  <td className={`${cellPad} text-right text-amber-400 bg-amber-500/5 tabular-nums`}>
                    {formatValue(projections[origin].ibnr)}
                  </td>
                  <td className={`${cellPad} text-right text-primary bg-primary/5 tabular-nums`}>
                    {projections[origin].cdf}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
        
        {/* Development Factors */}
        <tfoot>
          <tr className="border-t-2 border-border/50">
            <td colSpan={dev_periods.length + (showProjections ? 4 : 1)} className="py-2">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold px-3">Age-to-Age Factors (Link Ratios)</p>
            </td>
          </tr>
          {/* Method rows */}
          {Object.keys(all_methods[dev_pairs[0]] || {}).map(method => (
            <tr key={method} className="border-b border-border/20 bg-muted/5">
              <td className={`${cellPad} text-[9px] text-muted-foreground font-medium sticky left-0 bg-muted/5 capitalize`}>
                {method === '3year' ? '3-Year Avg' : method === '5year' ? '5-Year Avg' : method === 'medial_5x1' ? 'Medial 5×1' : method.charAt(0).toUpperCase() + method.slice(1) + ' Avg'}
              </td>
              {dev_periods.map((dev, di) => {
                if (di >= dev_periods.length - 1) return <td key={dev} className={cellPad}></td>;
                const pair = `${dev_periods[di]}-${dev_periods[di + 1]}`;
                const val = all_methods[pair]?.[method];
                return (
                  <td key={dev} className={`${cellPad} text-right tabular-nums text-[10px] text-muted-foreground border-r border-border/10`}>
                    {val !== undefined ? val.toFixed(4) : ''}
                  </td>
                );
              })}
              {showProjections && <td colSpan={3}></td>}
            </tr>
          ))}
          {/* Selected factors row */}
          <tr className="border-t-2 border-primary/30 bg-primary/5 font-bold">
            <td className={`${cellPad} text-[10px] text-primary font-semibold sticky left-0 bg-primary/5`}>
              Selected
            </td>
            {dev_periods.map((dev, di) => {
              if (di >= dev_periods.length - 1) return <td key={dev} className={cellPad}></td>;
              const pair = `${dev_periods[di]}-${dev_periods[di + 1]}`;
              const val = selected_factors[pair];
              return (
                <td key={dev} className={`${cellPad} text-right tabular-nums text-[10px] text-primary font-bold border-r border-border/10`}>
                  {val !== undefined ? val.toFixed(4) : ''}
                </td>
              );
            })}
            {showProjections && <td colSpan={3}></td>}
          </tr>
          {/* Totals */}
          <tr className="border-t-2 border-border/50 bg-muted/30">
            <td className={`${cellPad} font-bold text-foreground sticky left-0 bg-muted/30`}>Total</td>
            <td colSpan={dev_periods.length} className={cellPad}></td>
            {showProjections && (
              <>
                <td className={`${cellPad} text-right font-bold text-emerald-400 bg-emerald-500/10 border-l-2 border-emerald-500/20 tabular-nums`}>
                  {formatValue(totals.ultimate)}
                </td>
                <td className={`${cellPad} text-right font-bold text-amber-400 bg-amber-500/10 tabular-nums`}>
                  {formatValue(totals.ibnr)}
                </td>
                <td className={`${cellPad} text-right font-bold text-primary bg-primary/10 tabular-nums`}>
                </td>
              </>
            )}
          </tr>
        </tfoot>
      </table>
      
      <div className="flex items-center gap-3 px-3 py-2 text-[9px] text-muted-foreground border-t border-border/30 flex-wrap">
        <span>{origins.length} origin periods × {dev_periods.length} development periods</span>
        <Badge variant="outline" className="text-[8px]">
          {ultimates.method === 'bornhuetter_ferguson' ? 'Bornhuetter-Ferguson' : ultimates.method === 'cape_cod' ? 'Cape Cod' : 'Chain Ladder'}
        </Badge>
        <Badge variant="outline" className="text-[8px]">Tail: {ultimates.tail_factor}</Badge>
        {ultimates.cape_cod_elr && <Badge variant="outline" className="text-[8px] border-indigo-500/30 text-indigo-400">Cape Cod ELR: {(ultimates.cape_cod_elr * 100).toFixed(1)}%</Badge>}
        <label className="flex items-center gap-1 ml-auto cursor-pointer">
          <input type="checkbox" checked={showProjections} onChange={e => setShowProjections(e.target.checked)} className="w-3 h-3 rounded" />
          Show Projections
        </label>
      </div>
    </div>
  );
});
